import { urlBackend } from "@/shared";
import { erpApi } from "@/shared/axios";
import { useMutation } from "@tanstack/react-query";


const { post, get } = erpApi();

/**
 * Hook mutation para obtener reporte de comisiones de vendedores
 */
export const useObtenerReporteComisiones = () => {
  return useMutation({
    mutationFn: async (params: any) => {
      const resp = await post<any[]>(
        `${urlBackend}api/v1/obtener-reporte-comisiones/`,
        params,
        true
      );
      return resp;
    },
  });
};

/**
 * Hook para obtener grupos cargos asignados (GET)
 */
export const useObtenerGruposCargosAsignados = () => {
  return useMutation({
    mutationFn: async (params?: any) => {
      const resp = await get<any[]>(
        `${urlBackend}api/v1/obtener-comision-grupos-cargos-asignados/`,
        params,
        true
      );
      return resp;
    },
  });
};

/**
 * Hook mutation para obtener empleados y principal comisión (POST, params en JSON)
 */
export const useObtenerEmpleadosYPrincipalComision = () => {
  return useMutation({
    mutationFn: async (params: { cargos_ids: string; fecha_hasta: string }) => {
      const resp = await post<any[]>(
        `${urlBackend}api/v1/obtener-empleados-y-principal-comision/`,
        params,
        true
      );
      return resp;
    },
  });
};

/**
 * Interface para rango de comisión
 */
export interface RangoComision {
  id: number;
  min: string;
  max: string;
  porcentaje: string;
  fecha_inicio: string;
  fecha_fin: string | null;
  tipo: string;
}

/**
 * Hook mutation para obtener rangos de comisión (GET)
 */
export const useObtenerRangosComision = () => {
  return useMutation({
    mutationFn: async () => {
      const resp = await get<{ code: number; status: string; message: string; data: RangoComision[] }>(
        `${urlBackend}api/v1/obtener-rangos-comision/`,
        true
      );
      return resp;
    },
  });
};

/**
 * Interface para crear rango de comisión
 */
export interface CrearRangoComisionParams {
  tipo_id: string;
  min: string;
  max: string;
  porcentaje: string;
  fecha_inicio: string;
}

/**
 * Hook mutation para crear rango de comisión (POST)
 */
export const useCrearRangoComision = () => {
  return useMutation({
    mutationFn: async (params: CrearRangoComisionParams) => {
      const resp = await post<any>(
        `${urlBackend}api/v1/crear-rango-comision/`,
        params,
        true
      );
      return resp;
    },
  });
};

/**
 * Interface para tipo de comisión
 */
export interface TipoComision {
  id: number;
  nombre: string;
}

/**
 * Hook mutation para obtener tipos de comisión (GET)
 */
export const useObtenerTiposComision = () => {
  return useMutation({
    mutationFn: async () => {
      const resp = await get<{ code: number; status: string; message: string; data: TipoComision[] }>(
        `${urlBackend}api/v1/obtener-tipos-comision/`,
        true
      );
      return resp;
    },
  });
};

/**
 * Interface para distribuidor con tipo asignado
 */
export interface DistribuidorTipoAsignado {
  id: number;
  nombre_emp: string | null;
  tipo: string;
  nombre_grupo: string | null;
  fecha_inicio: string;
  fecha_fin: string | null;
}

/**
 * Hook mutation para obtener tipos asignados a distribuidores (GET)
 */
export const useObtenerTiposAsignadosDistribuidores = () => {
  return useMutation({
    mutationFn: async () => {
      const resp = await get<{ code: number; status: string; message: string; data: DistribuidorTipoAsignado[] }>(
        `${urlBackend}api/v1/obtener-tipos-cargos-comision/`,
        true
      );
      return resp;
    },
  });
};

/**
 * Interface para parámetros de asignar tipo a distribuidor
 */
export interface AsignarTipoDistribuidorParams {
  grupo_id: string;
  tipo_id: string;
  fecha_inicio: string;
}

/**
 * Hook mutation para asignar tipo a distribuidor (POST)
 */
export const useAsignarTipoDistribuidor = () => {
  return useMutation({
    mutationFn: async (params: AsignarTipoDistribuidorParams) => {
      const resp = await post<any>(
        `${urlBackend}api/v1/asignar-tipo-distribuidor/`,
        params,
        true
      );
      return resp;
    },
  });
};

/**
 * Hook mutation para procesar reporte de comisiones (POST)
 */
export const useProcesarReporteComision = () => {
  return useMutation({
    mutationFn: async (params: any) => {
      const resp = await post<any>(
        `${urlBackend}api/v1/procesar-reporte-comision/`,
        params,
        true
      );
      return resp;
    },
  });
};

/**
 * Interface para grupo de vendedores (del endpoint obtener-comision-grupos-cargos-asignados)
 */
export interface GrupoVendedor {
  grupo_id: string;
  grupo: string;
  cargo: string;
  cargos_ids: string;
  nombre_empleado: string;
}

/**
 * Hook mutation para obtener empleados por id del cargo (POST)
 */
export const useObtenerEmpleadosIdCargo = () => {
  return useMutation({
    mutationFn: async (cargos_ids: string) => {
      const resp = await post<any>(
        `${urlBackend}api/v1/obtener-empleados-id-cargo/`,
        { cargos_ids },
        true
      );
      return resp;
    },
  });
};